import { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { useTheme } from "../../../../context/ThemeProvider";
import {
  faviconGuessUrl,
  fetchMetadata,
  metadataFallback,
  safeFaviconUrl,
  type LinkMetadata,
} from "../../../../components/fetchMetadata";
import {
  hostnameFromUrl,
  titleFromUrl,
} from "../../../../utils/displayReadableUrl";

/**
 * Compact compose-time link card: square thumb left + title/host.
 * Cross → parent sends as plain text (no OG on web/mobile).
 */
export default function LinkDraftPreview({
  url,
  onDismiss,
  onMeta,
}: {
  url: string;
  onDismiss: () => void;
  /** Optimistic Webpage fields for send multipart */
  onMeta?: (meta: LinkMetadata) => void;
}) {
  const dark = useTheme().theme === "dark";
  const [meta, setMeta] = useState<LinkMetadata>(() => metadataFallback(url));
  const [faviconOk, setFaviconOk] = useState(false);
  const [ogReady, setOgReady] = useState(false);
  const [ogSrc, setOgSrc] = useState("");

  useEffect(() => {
    setMeta(metadataFallback(url));
    setFaviconOk(false);
    setOgReady(false);
    setOgSrc("");

    const guess = faviconGuessUrl(url);
    let gen = true;

    if (guess) {
      const probe = new Image();
      probe.onload = () => {
        if (!gen) return;
        setFaviconOk(true);
        setMeta((m) => ({ ...m, favicon: m.favicon || guess }));
      };
      probe.src = guess;
    }

    fetchMetadata(url).then((data) => {
      if (!gen) return;
      const nextFavicon = safeFaviconUrl(url, data.favicon || guess);
      const next: LinkMetadata = {
        title: data.title || titleFromUrl(url),
        description: data.description || "",
        publisher: data.publisher || hostnameFromUrl(url),
        favicon: nextFavicon,
        image: data.image || "",
      };
      setMeta(next);
      onMeta?.(next);
      if (nextFavicon) setFaviconOk(true);

      if (data.image) {
        const img = new Image();
        img.onload = () => {
          if (!gen) return;
          setOgSrc(data.image);
          setOgReady(true);
        };
        img.src = data.image;
      }
    });

    return () => {
      gen = false;
    };
  }, [url]);

  const title = meta.title || titleFromUrl(url);
  const host = meta.publisher || hostnameFromUrl(url);
  const faviconSrc = faviconOk ? meta.favicon || faviconGuessUrl(url) : "";

  return (
    <div
      className={`relative flex w-full items-center gap-2.5 overflow-hidden rounded-xl border p-2 pr-10 ${
        dark ? "border-[#EEEEEE14] bg-[#2a2a2a]" : "border-[#B0B0B0] bg-secondary"
      }`}
    >
      <button
        type="button"
        aria-label="Send without preview"
        onClick={onDismiss}
        className={`absolute right-1.5 top-1.5 z-10 grid h-7 w-7 place-content-center rounded-full ${dark ? "bg-[#1d1d1d]/80 text-white" : "bg-white/90 text-black"} shadow-sm`}
      >
        <RxCross2 className="h-4 w-4" />
      </button>

      <div
        className={`grid h-12 w-12 shrink-0 place-content-center overflow-hidden rounded-lg ${dark ? "bg-[#3a3a3a]" : "bg-[#e8e8e8]"}`}
      >
        {ogReady && ogSrc ? (
          <img
            src={ogSrc}
            alt=""
            width={48}
            height={48}
            decoding="async"
            className="h-12 w-12 object-cover"
          />
        ) : faviconSrc ? (
          <img
            src={faviconSrc}
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 object-contain"
            onError={() => setFaviconOk(false)}
          />
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={dark ? "#9aa0a6" : "#5f6368"}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-snug">{title}</p>
        {host ? (
          <p className="mt-0.5 truncate text-[11px] font-medium text-[#027EB5]">
            {host}
          </p>
        ) : null}
      </div>
    </div>
  );
}
